import {
  Breadcrumb,
  BreadcrumbIcon,
  BreadcrumbLink,
  BreadcrumbPage,
  Button,
} from '@ads/components-react';
import { ArrowLeftIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { ContractStatusBadge } from '../../atoms/ContractStatusBadge';

/** Props de `ParcelEditTemplate`. */
export interface ParcelEditTemplateProps {
  /** ID do contrato como veio da rota — usado nos links do breadcrumb. */
  readonly financialPersonId: string;
  /** Nome do aluno no breadcrumb. */
  readonly studentName: string;
  /** Identificação da parcela e do contrato, no subtítulo. */
  readonly subtitle: string;
  /** Status da parcela, exibido como Tag ao lado do título. */
  readonly statusLabel?: string | null | undefined;
  /** Régua de ações da parcela, entre o cabeçalho e as seções. */
  readonly toolbar?: ReactNode | undefined;
  /** Seções do formulário. */
  readonly children: ReactNode;
  /** Dispara a gravação. */
  readonly onSave: () => void;
  /** Descarta e volta para as parcelas. */
  readonly onCancel: () => void;
  readonly isSaving: boolean;
  /**
   * Envio liberado.
   *
   * Hoje é sempre `false` na prática: a semeadura vem da grid do contrato, que não
   * cobre seis campos do payload, e o `PUT` substitui o registro. Bloquear o botão
   * é o que impede que um Salvar sem edição nenhuma apague esses campos.
   */
  readonly canSave: boolean;
  /** Explicação do bloqueio, exibida no rodapé quando `canSave` é `false`. */
  readonly blockedReason?: string | undefined;
}

/**
 * Casca da tela de edição de parcela.
 *
 * Espelha a tela de criação — breadcrumb, cabeçalho com título e ação de volta,
 * seções em cards e régua Cancelar/Salvar fora dos cards — e acrescenta dois
 * elementos que só a edição tem: a Tag de status ao lado do título, porque o
 * estado da parcela muda o que o backend aceita alterar, e o slot da régua de
 * ações da parcela.
 *
 * Segue o frame `Upir5` de `design/payment.pen`.
 */
export function ParcelEditTemplate({
  financialPersonId,
  studentName,
  subtitle,
  statusLabel,
  toolbar,
  children,
  onSave,
  onCancel,
  isSaving,
  canSave,
  blockedReason,
}: ParcelEditTemplateProps) {
  const { t } = useTranslation('individualPayment');

  return (
    <div className="flex flex-col gap-xs">
      <div className="flex flex-col gap-mini">
        {/* Só o último segmento é `BreadcrumbPage`: quem emite o ">" é o
            `BreadcrumbLink`, então um `BreadcrumbPage` no meio engole o separador. */}
        <Breadcrumb>
          <BreadcrumbLink asChild>
            <Link to="/">
              <BreadcrumbIcon />
            </Link>
          </BreadcrumbLink>
          <BreadcrumbLink asChild>
            <Link to="/individual-payment">{t('planEdit.breadcrumb.root')}</Link>
          </BreadcrumbLink>
          <BreadcrumbLink asChild>
            <Link
              to="/individual-payment/$financialPersonId/parcels"
              params={{ financialPersonId }}
            >
              {studentName}
            </Link>
          </BreadcrumbLink>
          <BreadcrumbPage>{t('parcelEdit.breadcrumb.current')}</BreadcrumbPage>
        </Breadcrumb>

        <div className="flex flex-wrap items-center gap-mini">
          <div className="flex flex-1 flex-col gap-quark">
            <span className="flex flex-wrap items-center gap-micro">
              <h1 className="ts-heading-md text-ctx-content-title">{t('parcelEdit.title')}</h1>
              <ContractStatusBadge status={statusLabel} />
            </span>
            <p className="ts-paragraph-xs text-ctx-content-placeholder">{subtitle}</p>
          </div>
          <Button hierarchy="secondary" size="md" leadingIcon={ArrowLeftIcon} onClick={onCancel}>
            {t('parcelCreate.backToParcels')}
          </Button>
        </div>
      </div>

      {toolbar}

      {children}

      <div className="flex flex-wrap items-center gap-mini rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface px-xxs py-mini">
        {blockedReason === undefined ? null : (
          <span className="flex flex-1 items-center justify-start gap-nano">
            <InformationCircleIcon className="size-xxs shrink-0 text-ctx-content-placeholder" />
            <span className="ts-paragraph-xxs text-ctx-content-placeholder">{blockedReason}</span>
          </span>
        )}
        <span className="flex flex-1 justify-end gap-mini">
          <Button hierarchy="secondary" size="md" disabled={isSaving} onClick={onCancel}>
            {t('parcelCreate.footer.cancel')}
          </Button>
          <Button
            hierarchy="primary"
            size="md"
            isLoading={isSaving}
            disabled={isSaving || !canSave}
            onClick={onSave}
          >
            {isSaving ? t('parcelCreate.footer.saving') : t('parcelEdit.footer.save')}
          </Button>
        </span>
      </div>
    </div>
  );
}
